import React from 'react'
import i18next, { StringMap, TOptions } from 'i18next'
import { useTranslation, initReactI18next } from 'react-i18next'
import { isServer } from '@/lib/utils'
import { useCurrentLanguage } from './current-language'
export type TSupportedLocales = 'en' | 'id'

i18next.use(initReactI18next).init({
  resources: { id: { ns: {} } },
  lowerCaseLng: true,
  fallbackLng: 'id',
  supportedLngs: ['id', 'en'],
  interpolation: {
    escapeValue: false,
  },
})

if (!isServer) {
  ;(window as any).i18next = i18next
}

const ResourceSymbol = Symbol('i18nextResource')

type IResource<T extends object> = {
  [lng in TSupportedLocales]: T
} & {
  [ResourceSymbol]?: string
}

type TFunction<T extends object> = <K extends keyof T, TResult = T[K], TInterpolationMap extends object = StringMap>(
  key: K,
  options?: TOptions<TInterpolationMap> | string,
) => TResult

function useResource<T extends object>(resource: IResource<T>) {
  if (!resource[ResourceSymbol]) throw Error('Resource must be created using createResource function')
  const [isResourceLoaded, setIsResourceLoaded] = React.useState(false)
  if (!isResourceLoaded) {
    Object.entries(resource).forEach(([lng, keyValue]) => {
      if (!i18next.hasResourceBundle(lng, resource[ResourceSymbol]!)) {
        i18next.addResourceBundle(lng, resource[ResourceSymbol]!, keyValue, true, true)
      }
    })
    setIsResourceLoaded(true)
  }
  const { language: lng } = useCurrentLanguage()
  const useTranslationFn = useTranslation(resource[ResourceSymbol]).t
  const t = (field: string, options: object) => useTranslationFn(field, Object.assign({}, { ...options }, { lng }))
  return { t } as { t: TFunction<T> }
}

let ts = Date.now()

const createResource = <T extends object>(resource: IResource<T>) => {
  Object.assign(resource, { [ResourceSymbol]: (ts++).toString() })
  return resource
}

export { i18next, useResource, createResource }
